import { useEffect, useState, useMemo, useCallback } from 'react'
import { useUser } from '@fs/zion-user'
import useChatFlags from './useChatFlags'
import { CHAT_ACTIONS } from './useChatReducer'
import { CHAT_PREF, getChatPreference } from '../services/chatUserPreferenceService/chatUserPreferenceService'
import { updateChannelInChannels } from '../helpers/stream/streamChatHelper'
import useChatLog from './useChatLog'
import { LOG_LEVEL } from '../global/chatLog'
import { getIsChannelMember } from '../services/fsChat/fsChatService'
import { CHANNEL_TYPE } from '../services/fsChat/fsChannelEnums'

/*
Hooks for initializing chat state in ChatStateProvider
*/

// Fetches and sets preferences for the showEvents, darkMode, and sound toggles
const useLoadChatPreferences = ({ chatDispatch, isEmbedded, cisId }) => {
  useEffect(() => {
    let isUnmounted = false
    async function getToggleData() {
      const [enterSendsMessage] = await Promise.all([getChatPreference(CHAT_PREF.ENTER_SENDS_MESSAGE, true)])
      if (isUnmounted) return
      chatDispatch({ type: CHAT_ACTIONS.ENTER_SENDS_MESSAGE, enterSendsMessage })

      // These are not needed for embedded chat
      if (!isEmbedded) {
        const [showEvents, darkMode, soundEnabled] = await Promise.all([
          getChatPreference(CHAT_PREF.SHOW_EVENT_CHATS, true),
          getChatPreference(CHAT_PREF.DARK_MODE, false),
          getChatPreference(CHAT_PREF.SOUND_ENABLED, true),
        ])
        if (isUnmounted) return
        chatDispatch({ type: CHAT_ACTIONS.SHOW_EVENTS, showEvents })
        chatDispatch({ type: CHAT_ACTIONS.DARK_MODE, darkMode })
        chatDispatch({ type: CHAT_ACTIONS.SET_SOUND_ENABLED, soundEnabled })
      }
    }

    if (cisId) {
      getToggleData()
    }
    return () => {
      isUnmounted = true
    }
  }, [chatDispatch, cisId, isEmbedded])
}

// Determines whether to render the fallback for embedded chat
const useShouldRenderFallback = ({ user, chatDispatch }) => {
  const { messagingChatShowEmbeddedFallbackForAllIsOn } = useChatFlags()
  useEffect(() => {
    const renderFallback = !user.signedIn || messagingChatShowEmbeddedFallbackForAllIsOn
    chatDispatch({
      type: CHAT_ACTIONS.SET_RENDER_FALLBACK,
      renderFallback,
    })
  }, [chatDispatch, user.signedIn, messagingChatShowEmbeddedFallbackForAllIsOn])
}

// Synchronizes local channel list with Stream's server state when real-time events
// (new messages, added to a channel, invitations) indicate a channel should be in memory but isn't.
// Triggered by channelIdToQuery being set from WebSocket events.
const useUpdateCurrentChannel = ({ chatState, chatDispatch, isEmbedded, overlayLoaded }) => {
  const { client, channels, channelIdToQuery, unreadChannels } = chatState
  const { messagingChatQueryUnreadsIsOn } = useChatFlags()
  const { log } = useChatLog({ component: '[useUpdateCurrentChannel]' })
  const [isUpdating, setIsUpdating] = useState(false)

  const shouldUpdate = useMemo(() => {
    return (isEmbedded || overlayLoaded) && client && channelIdToQuery && !isUpdating
  }, [isEmbedded, overlayLoaded, client, channelIdToQuery, isUpdating])

  const updateChannel = useCallback(
    async (channelId) => {
      if (!channelId || isUpdating) return

      setIsUpdating(true)
      chatDispatch({ type: CHAT_ACTIONS.CLEAR_CHANNEL_ID_TO_QUERY })

      try {
        const [channel] = await client.queryChannels({ cid: { $in: [channelId] } })

        if (!channel) {
          log({ error: `No channel found for ID: ${channelId}`, level: LOG_LEVEL.WARN })
          return
        }

        const chatChannels = updateChannelInChannels(channels, channel, true)
        chatDispatch({ type: CHAT_ACTIONS.SET_CHANNELS, channels: chatChannels })

        if (messagingChatQueryUnreadsIsOn) {
          const unreadChatChannels = updateChannelInChannels(unreadChannels, channel, true)
          chatDispatch({ type: CHAT_ACTIONS.SET_UNREAD_CHANNELS, unreadChannels: unreadChatChannels })
        }
      } catch (err) {
        log({ error: `Error updating current channel: ${err}`, level: LOG_LEVEL.ERROR })
      } finally {
        setIsUpdating(false)
      }
    },
    [client, channels, unreadChannels, chatDispatch, messagingChatQueryUnreadsIsOn, log, isUpdating]
  )

  useEffect(() => {
    if (shouldUpdate) {
      updateChannel(channelIdToQuery)
    }
  }, [shouldUpdate, channelIdToQuery, updateChannel])
}

const useShouldConnectAnonymously = ({ isEmbedded, chatRoute, supportChatInformation }) => {
  const { userLoading, signedIn } = useUser()
  const isSessionChannel = chatRoute?.includes('session')
  const { messagingChatAnonymousConnectIsOn } = useChatFlags()
  const canConnectAnonymously = isEmbedded && signedIn && isSessionChannel && messagingChatAnonymousConnectIsOn
  const [shouldConnectAnonymously, setShouldConnectAnonymously] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let isLatestRender = true
    async function getIsChannelMemberEffect() {
      if (userLoading) return
      if (!canConnectAnonymously) {
        setShouldConnectAnonymously(false)
        setLoading(false)
        return
      }
      const isMember = await getIsChannelMember({
        channelType: CHANNEL_TYPE.FS_SESSION,
        supportInformation: supportChatInformation,
      })
      if (isLatestRender) {
        if (isMember) {
          setShouldConnectAnonymously(false)
        } else {
          setShouldConnectAnonymously(true)
        }
        setLoading(false)
      }
    }
    getIsChannelMemberEffect()
    return () => {
      isLatestRender = false
    }
  }, [supportChatInformation, canConnectAnonymously, userLoading])

  return [shouldConnectAnonymously, loading]
}

export { useLoadChatPreferences, useUpdateCurrentChannel, useShouldRenderFallback, useShouldConnectAnonymously }
